import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchTutorialStatus as fetchTutorialStatusAPI, updateTutorialStatus as updateTutorialStatusAPI } from '../services/tutorialApi';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('tutorialSlice');

const TUTORIAL_STORAGE_KEY = 'loyaltap_tutorial_state';

// Tutorial steps configuration
export const TUTORIAL_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to LoyalPass!',
        description: 'Get ready to transform your customer loyalty program! 🎉 LoyalPass makes it super easy to create and manage digital wallet passes that your customers will love. Let\'s get you set up in just a few simple steps!',
        route: '/dashboard',
        targetSelector: null,
        completionCheck: null,
        isWelcomeScreen: true,
    },
    {
        id: 'settings',
        title: 'Complete Your Settings',
        description: 'Let\'s get your store set up! 🎨 Fill out your store name (super important!), phone number, address, website, Google Maps link, and socials. Don\'t forget to add your branding—pick your brand colors and upload your logo (must be hosted online)! This info will make your passes look on-brand and amazing!',
        route: '/dashboard/settings/general',
        targetSelector: '[data-tutorial="settings-form"]',
        completionCheck: 'settings_completed',
    },
    {
        id: 'android-pass',
        title: 'Create Android Pass Template',
        description: 'Time to create your Google Wallet pass template! 🚀 This is where the magic happens - set up your reward program name and pass colors. Pro tip: Make sure it looks good on the preview before creating the template. Remember this can only be done once!',
        route: '/dashboard/settings/android-pass',
        targetSelector: '[data-tutorial="android-pass-form"]',
        completionCheck: 'android_pass_created',
    },
    {
        id: 'apple-pass',
        title: 'Create Apple Pass Template',
        description: 'Time to create your Apple Wallet pass template! 🚀 This is where the magic happens - set up your reward program name and pass colors. Pro tip: Make sure it looks good on the preview before creating the template. Remember this can only be done once!',
        route: '/dashboard/settings/apple-pass',
        targetSelector: '[data-tutorial="apple-pass-form"]',
        completionCheck: 'apple_pass_created',
    },
    {
        id: 'create-reward',
        title: 'Create Your First Reward',
        description: 'This is the fun part! 🎁 Create awesome rewards that your customers can redeem with their points! Click "Add Reward" and add an exciting reward to keep your customers engaged. Set the title, description, and point cost. Your customers will appreciate a place that rewards them for spending!',
        route: '/dashboard/rewards',
        targetSelector: '[data-tutorial="create-reward-button"]',
        completionCheck: 'reward_created',
    },
    {
        id: 'generate-pass',
        title: 'Generate a Pass for a Customer',
        description: 'Ready to create your first pass? 🎉 Enter a customer\'s email to check if they\'re in your system. New customer? No problem! Just fill in their info (name, phone, and their birthday for special rewards!). Pick Apple or Android, hit Generate—LoyalPass will email the pass, or the customer can scan the on-screen QR code with their phone to download it.',
        route: '/dashboard/generate',
        targetSelector: '[data-tutorial="generate-email-input"]',
        completionCheck: 'pass_generated',
    },
    {
        id: 'customers',
        title: 'View Your Customers',
        description: 'Meet your customers! 👥 See everyone who\'s part of your loyalty program at a glance - their platform, email, points, visits, and more! Dive into their details, check their activity history, and manage their passes. Use filters and search to find anyone in seconds!',
        route: '/dashboard/customers',
        targetSelector: '[data-tutorial="customers-page"]',
        completionCheck: 'customers_viewed',
    },
    {
        id: 'scan',
        title: 'Scan and Manage Customer Pass',
        description: 'This is where the action happens! 🔍 When customers visit your store, search for them by email or scan their QR code. Add points, record visits, track their spending, and redeem rewards - all in one place! This is your command center for customer interactions.',
        route: '/dashboard/scan',
        targetSelector: '[data-tutorial="scan-email-input"]',
        completionCheck: 'scan_viewed',
    },
    {
        id: 'marketing',
        title: 'Explore Marketing Features',
        description: 'Let\'s get the word out! 📧 Create and send amazing email campaigns to your customers! Announce special promotions, send birthday wishes, or share exciting news. You can even segment your customers and track how engaged they are. Time to grow your business!',
        route: '/dashboard/marketing',
        targetSelector: '[data-tutorial="marketing-page"]',
        completionCheck: 'marketing_viewed',
    },
    {
        id: 'staff',
        title: 'Manage Your Staff',
        description: 'Grow your team with confidence! 👥 Invite staff members, assign roles, and control permissions so everyone has the right level of access. Keep your operations secure while making collaboration easier.',
        route: '/dashboard/staff',
        targetSelector: '[data-tutorial="staff-page"]',
        completionCheck: 'staff_viewed',
    },
    {
        id: 'dashboard',
        title: 'Your Dashboard Overview',
        description: 'This is your command center! 📊 Switch time ranges (Total, Year, Month, Week, Day) to see stats and charts for that period. Use Marketing to run campaigns. Top Customers can be viewed by current Points, Visits, or Spending. Most Redeemed Rewards shows what\'s popular at your store.',
        route: '/dashboard',
        targetSelector: '[data-tutorial="dashboard-overview"]',
        completionCheck: 'dashboard_viewed',
    },
    {
        id: 'completion',
        title: 'Congratulations!',
        description: 'You did it! 🎊 You\'ve completed the LoyalPass tutorial and you\'re all set to start building an amazing loyalty program! Your customers are going to love the experience you\'re about to create. Let\'s make some magic happen!',
        route: '/dashboard',
        targetSelector: null,
        completionCheck: null,
        isCompletionScreen: true,
    },
];

// Load tutorial state from localStorage
const loadTutorialState = () => {
    try {
        const saved = localStorage.getItem(TUTORIAL_STORAGE_KEY);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (error) {
        logger.error('Error loading tutorial state', error);
    }
    return null;
};

const savedState = loadTutorialState();

export const initialState = {
    isActive: savedState?.isActive ?? false,
    currentStep: savedState?.currentStep ?? 0,
    completedSteps: savedState?.completedSteps ?? [],
    isComplete: savedState?.isComplete ?? false,
    loading: false,
    error: null,
};

// Async thunks for API calls
export const fetchTutorialStatus = createAsyncThunk(
    'tutorial/fetchTutorialStatus',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetchTutorialStatusAPI();
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateTutorialStatus = createAsyncThunk(
    'tutorial/updateTutorialStatus',
    async (tutorial_completed, { rejectWithValue }) => {
        try {
            const res = await updateTutorialStatusAPI(tutorial_completed);
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const tutorialSlice = createSlice({
    name: 'tutorial',
    initialState,
    reducers: {
        startTutorial: (state) => {
            state.isActive = true;
            state.currentStep = 0;
            state.completedSteps = [];
            state.isComplete = false;
            saveTutorialState(state);
        },
        nextStep: (state) => {
            if (state.currentStep < TUTORIAL_STEPS.length - 1) {
                state.currentStep += 1;
                saveTutorialState(state);
            }
        },
        previousStep: (state) => {
            if (state.currentStep > 0) {
                state.currentStep -= 1;
                saveTutorialState(state);
            }
        },
        goToStep: (state, action) => {
            const step = action.payload;
            if (step >= 0 && step < TUTORIAL_STEPS.length) {
                state.currentStep = step;
                saveTutorialState(state);
            }
        },
        completeStep: (state, action) => {
            const stepIndex = action.payload;
            if (!state.completedSteps.includes(stepIndex)) {
                state.completedSteps.push(stepIndex);
                saveTutorialState(state);
            }
        },
        completeTutorial: (state) => {
            state.isActive = false;
            state.isComplete = true;
            saveTutorialState(state);
        },
        skipTutorial: (state) => {
            state.isActive = false;
            state.isComplete = true;
            saveTutorialState(state);
        },
        setTutorialCompleteFromDB: (state, action) => {
            // Sync with database status
            state.isComplete = action.payload;
            if (action.payload) {
                state.isActive = false;
            }
            saveTutorialState(state);
        },
        resetTutorial: (state) => {
            state.isActive = false;
            state.currentStep = 0;
            state.completedSteps = [];
            state.isComplete = false;
            localStorage.removeItem(TUTORIAL_STORAGE_KEY);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTutorialStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTutorialStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                // Sync with database status
                state.isComplete = action.payload.tutorial_completed ?? false;
                if (state.isComplete) {
                    state.isActive = false;
                }
                saveTutorialState(state);
            })
            .addCase(fetchTutorialStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch tutorial status';
            })
            .addCase(updateTutorialStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTutorialStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                // Sync with database status
                state.isComplete = action.payload.tutorial_completed ?? false;
                if (state.isComplete) {
                    state.isActive = false;
                }
                saveTutorialState(state);
            })
            .addCase(updateTutorialStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update tutorial status';
            });
    },
});

// Save tutorial state to localStorage
const saveTutorialState = (state) => {
    try {
        localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
        logger.error('Error saving tutorial state', error);
    }
};

export const { startTutorial, nextStep, previousStep, goToStep, completeStep, completeTutorial, skipTutorial, resetTutorial, setTutorialCompleteFromDB } = tutorialSlice.actions;

// Export API thunks for convenience
export const tutorialSliceAPI = {
    fetchTutorialStatus,
    updateTutorialStatus,
};

export default tutorialSlice.reducer;

